import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  createChapter,
  getPublicChapters,
  grantRangeAccess,
  listChapters,
  listRangeAccess,
  updateChapter,
  updateRangeAccess
} from "../lib/rangegridApi";

const CHAPTER_LEADER_APPLICATION_SCOPES = ["chapters", "membership", "events", "staff-portal", "communications"];
const CHAPTER_TAB_IDS = ["directory", "settings"];
const CHAPTER_MEMBER_VISIBILITY_FIELDS = [
  {
    key: "chapterMemberVisibilityPhone",
    label: "Phone",
    detail: "Let chapter leads call or text local members."
  },
  {
    key: "chapterMemberVisibilityAddress",
    label: "Address",
    detail: "Show the member mailing address."
  },
  {
    key: "chapterMemberVisibilityStatus",
    label: "Membership status",
    detail: "Show active, inactive, and renewal state."
  },
  {
    key: "chapterMemberVisibilityJoinDate",
    label: "Join date",
    detail: "Show when the member joined."
  },
  {
    key: "chapterMemberVisibilityMemberNumber",
    label: "Member number",
    detail: "Show the internal member number."
  }
];

function defaultChapterDraft() {
  return {
    name: "",
    slug: "",
    status: "active",
    publicVisible: true,
    description: "",
    city: "",
    region: "",
    meetingArea: "",
    venueName: "",
    address: {
      line1: "",
      line2: "",
      city: "",
      region: "",
      postalCode: "",
      country: "US"
    },
    timezone: "",
    contactEmail: "",
    contactPhone: "",
    publicUrl: "",
    sourceUrl: "",
    summary: "",
    leaderFirstName: "",
    leaderLastName: "",
    leaderPhone: "",
    leaderEmailsText: ""
  };
}

function chapterTabFromHash(hash = "") {
  const normalized = String(hash || "").replace(/^#chapters-/, "").toLowerCase();
  return CHAPTER_TAB_IDS.includes(normalized) ? normalized : "directory";
}

function chapterToDraft(chapter) {
  return {
    name: chapter.name || "",
    slug: chapter.slug || "",
    status: chapter.status || "active",
    publicVisible: chapter.publicVisible !== false,
    description: chapter.description || "",
    city: chapter.city || "",
    region: chapter.region || "",
    meetingArea: chapter.meetingArea || "",
    venueName: chapter.venueName || "",
    address: {
      line1: chapter.address?.line1 || "",
      line2: chapter.address?.line2 || "",
      city: chapter.address?.city || "",
      region: chapter.address?.region || "",
      postalCode: chapter.address?.postalCode || "",
      country: chapter.address?.country || "US"
    },
    timezone: chapter.timezone || "",
    contactEmail: chapter.contactEmail || "",
    contactPhone: chapter.contactPhone || "",
    publicUrl: chapter.publicUrl || "",
    sourceUrl: chapter.sourceUrl || "",
    summary: chapter.summary || "",
    leaderFirstName: chapter.leaderFirstName || "",
    leaderLastName: chapter.leaderLastName || "",
    leaderPhone: chapter.leaderPhone || "",
    leaderEmailsText: (chapter.leaderEmails || []).join("\n")
  };
}

function draftToPayload(draft) {
  return {
    name: draft.name,
    slug: draft.slug,
    status: draft.status,
    publicVisible: draft.publicVisible,
    description: draft.description,
    city: draft.city,
    region: draft.region,
    meetingArea: draft.meetingArea,
    venueName: draft.venueName,
    address: draft.address,
    timezone: draft.timezone,
    contactEmail: draft.contactEmail,
    contactPhone: draft.contactPhone,
    publicUrl: draft.publicUrl,
    sourceUrl: draft.sourceUrl,
    summary: draft.summary,
    leaderFirstName: draft.leaderFirstName,
    leaderLastName: draft.leaderLastName,
    leaderPhone: draft.leaderPhone,
    leaderEmails: String(draft.leaderEmailsText || "")
      .split(/[\n,]+/)
      .map((item) => item.trim())
      .filter(Boolean)
  };
}

function chapterLocation(chapter) {
  return [chapter.city, chapter.region].filter(Boolean).join(", ") || chapter.venueName || chapter.meetingArea || "Location unavailable";
}

function formatChapterAddress(address = {}) {
  const line1 = String(address.line1 || "").trim();
  const line2 = String(address.line2 || "").trim();
  const cityLine = [
    address.city,
    address.region,
    address.postalCode
  ].filter(Boolean).join(", ");
  return [line1, line2, cityLine, address.country].filter(Boolean).join(" / ") || "Unavailable";
}

function publicPath(chapter) {
  return chapter.slug ? `/${chapter.slug}` : "Unavailable";
}

function publicChapterToOwnerChapter(chapter) {
  return {
    ...chapter,
    status: chapter.status || "active",
    publicVisible: chapter.publicVisible !== false,
    leaderEmails: chapter.leaderEmails || [],
    leaderUserIds: chapter.leaderUserIds || [],
    ownerDataSource: "public-directory"
  };
}

function mergeChapterDirectories(ownerChapters = [], publicChapters = []) {
  const byId = new Map();
  publicChapters
    .map(publicChapterToOwnerChapter)
    .forEach((chapter) => {
      if (chapter.chapterId) {
        byId.set(chapter.chapterId, chapter);
      }
    });
  ownerChapters.forEach((chapter) => {
    if (chapter.chapterId) {
      byId.set(chapter.chapterId, {
        ...chapter,
        ownerDataSource: chapter.ownerDataSource || "owner"
      });
    }
  });
  return Array.from(byId.values())
    .sort((left, right) => {
      if (left.status !== right.status) {
        return left.status === "active" ? -1 : 1;
      }
      return String(left.name || "").localeCompare(String(right.name || ""));
    });
}

function eventChapterFallbacks(events = []) {
  const byId = new Map();
  events.forEach((event) => {
    const snapshot = event?.chapterSnapshot || {};
    const chapterIds = Array.isArray(event?.chapterIds)
      ? event.chapterIds
      : event?.chapterId
        ? [event.chapterId]
        : [];
    chapterIds
      .map((chapterId) => String(chapterId || "").trim())
      .filter(Boolean)
      .forEach((chapterId) => {
        if (!byId.has(chapterId)) {
          byId.set(chapterId, {
            chapterId,
            name: snapshot.name || "Chapter",
            slug: snapshot.slug,
            status: "active",
            publicVisible: snapshot.publicVisible !== false,
            description: snapshot.description || "",
            city: snapshot.city,
            region: snapshot.region,
            meetingArea: event.location || snapshot.meetingArea || "",
            contactEmail: snapshot.contactEmail || "",
            leaderEmails: [],
            leaderUserIds: [],
            ownerDataSource: "event"
          });
        }
      });
  });
  return Array.from(byId.values());
}

function titleFromEmail(email = "") {
  const localPart = String(email || "").split("@")[0] || "";
  return localPart
    .split(/[._-]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .filter(Boolean)
    .join(" ") || "Chapter lead";
}

function chapterLead(chapter = {}) {
  const email = (chapter.leaderEmails || []).find(Boolean) || "";
  const name = [chapter.leaderFirstName, chapter.leaderLastName].filter(Boolean).join(" ")
    || chapter.leaderName
    || chapter.leaderNames?.[0]
    || (email ? titleFromEmail(email) : "");
  const phone = chapter.leaderPhone || "";
  if (email || name || phone) {
    return {
      available: true,
      name: name || "Assigned lead",
      email: email || "Email unavailable",
      phone: phone || "Phone unavailable"
    };
  }
  if ((chapter.leaderUserIds || []).length > 0) {
    return {
      available: true,
      name: "Assigned lead",
      email: "Email unavailable",
      phone: "Phone unavailable"
    };
  }
  return {
    available: false,
    name: "Unavailable",
    email: "Unavailable",
    phone: "Unavailable"
  };
}

function availableLeadValue(value) {
  const text = String(value || "").trim();
  return text && text !== "Unavailable" && text !== "Email unavailable" && text !== "Phone unavailable";
}

function accessStatusLabel(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function chapterLeaderAccess(chapter, accessRecords = []) {
  if (!chapter?.chapterId) {
    return null;
  }
  const leadEmail = (chapter.leaderEmails || []).find(Boolean)?.toLowerCase();
  return accessRecords.find((access) => {
    if (access.role !== "chapter_leader") {
      return false;
    }
    const chapterScoped = (access.chapterIds || []).includes(chapter.chapterId);
    const emailMatches = leadEmail && String(access.email || "").toLowerCase() === leadEmail;
    return chapterScoped && (!leadEmail || emailMatches);
  }) || null;
}

function chapterLeaderAccessByEmail(email, accessRecords = []) {
  const targetEmail = String(email || "").trim().toLowerCase();
  if (!targetEmail) {
    return null;
  }
  return accessRecords.find((access) => (
    access.role === "chapter_leader" &&
    String(access.email || "").trim().toLowerCase() === targetEmail
  )) || null;
}

function chapterVisibility(chapter = {}) {
  if (chapter.status && chapter.status !== "active") {
    return "Inactive";
  }
  return chapter.publicVisible === false ? "Private" : "Public";
}

function memberChapterIds(member = {}) {
  return [
    member.homeChapterId,
    member.chapterId,
    member.chapterSnapshot?.chapterId,
    ...(Array.isArray(member.chapterIds) ? member.chapterIds : [])
  ]
    .map((chapterId) => String(chapterId || "").trim())
    .filter(Boolean);
}

function memberMatchesChapter(member, chapterId) {
  return memberChapterIds(member).includes(chapterId);
}

function memberStatus(member = {}) {
  return member.expiration?.effectiveStatus || member.status || "member";
}

function memberStatusLabel(value = "") {
  return String(value || "member")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function memberJoinedAt(member = {}) {
  return (
    member.chapterMembership?.joinedAt ||
    member.chapterMemberships?.[0]?.joinedAt ||
    member.membership?.joinedAt ||
    member.membership?.currentTermStart ||
    member.joinedAt ||
    member.createdAt ||
    ""
  );
}

function formatMemberDate(value) {
  if (!value) {
    return "Unavailable";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unavailable";
  }
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  }).format(date);
}

function defaultChapterMemberDraft(chapter = {}, membershipSummary = {}) {
  const defaultMembershipTypeId =
    membershipSummary?.config?.defaultMembershipTypeId ||
    (membershipSummary?.membershipTypes || []).find((type) => type.status !== "inactive")?.membershipTypeId ||
    "";
  return {
    fullName: "",
    email: "",
    phone: "",
    address: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "US"
    },
    status: "active",
    membershipTypeId: defaultMembershipTypeId,
    homeChapterId: chapter?.chapterId || "",
    chapterIds: chapter?.chapterId ? [chapter.chapterId] : [],
    chapterSnapshot: chapter?.chapterId
      ? {
          chapterId: chapter.chapterId,
          name: chapter.name,
          slug: chapter.slug,
          city: chapter.city,
          region: chapter.region,
          publicVisible: chapter.publicVisible
        }
      : undefined
  };
}

function memberEditDraft(member = {}) {
  const address = member.address || {};
  return {
    fullName: member.fullName || "",
    email: member.email || "",
    phone: member.phone || "",
    address: {
      line1: address.line1 || "",
      line2: address.line2 || "",
      city: address.city || "",
      state: address.state || address.region || "",
      postalCode: address.postalCode || "",
      country: address.country || "US"
    }
  };
}

function memberAddressPayload(address = {}) {
  return {
    line1: String(address.line1 || "").trim(),
    line2: String(address.line2 || "").trim(),
    city: String(address.city || "").trim(),
    state: String(address.state || "").trim(),
    postalCode: String(address.postalCode || "").trim(),
    country: String(address.country || "US").trim().toUpperCase() || "US"
  };
}

function formatMemberAddress(address = {}) {
  const line1 = String(address.line1 || "").trim();
  const line2 = String(address.line2 || "").trim();
  const cityLine = [
    address.city,
    address.state || address.region,
    address.postalCode
  ].filter(Boolean).join(", ");
  return [line1, line2, cityLine, address.country].filter(Boolean).join(" / ") || "Unavailable";
}

function chapterMatchesFilters(chapter, filters = {}) {
  const query = String(filters.q || "").trim().toLowerCase();
  const visibility = filters.visibility || "";
  if (visibility === "public" && (chapter.status !== "active" || chapter.publicVisible === false)) {
    return false;
  }
  if (visibility === "private" && chapter.publicVisible !== false) {
    return false;
  }
  if (visibility === "inactive" && chapter.status === "active") {
    return false;
  }
  if (!query) {
    return true;
  }
  const lead = chapterLead(chapter);
  const haystack = [
    chapter.name,
    chapter.city,
    chapter.region,
    chapter.meetingArea,
    chapter.venueName,
    chapter.address?.line1,
    chapter.address?.city,
    chapter.contactEmail,
    chapter.contactPhone,
    chapter.summary,
    lead.name,
    lead.email,
    lead.phone
  ].filter(Boolean).join(" ").toLowerCase();
  return haystack.includes(query);
}

export function ChaptersPage({
  events = [],
  members = [],
  membersLoading = false,
  membershipConfigForm = {},
  membershipSummary,
  onCreateMember,
  onSaveMembershipConfig,
  onUpdateMemberProfile,
  onUpdateMemberStatus,
  savingMember = false,
  savingMembershipConfig = false,
  selectedRange,
  setMembershipConfigForm,
  updatingMemberProfileId = "",
  updatingMemberStatusId = ""
}) {
  const { rangeId: routeRangeId = "" } = useParams();
  const [chapters, setChapters] = useState([]);
  const [filters, setFilters] = useState({ q: "", visibility: "" });
  const [draft, setDraft] = useState(defaultChapterDraft);
  const [activeChapterTab, setActiveChapterTab] = useState(() => (
    typeof window === "undefined" ? "directory" : chapterTabFromHash(window.location.hash)
  ));
  const [chapterViewMode, setChapterViewMode] = useState("list");
  const [editingChapterId, setEditingChapterId] = useState("");
  const [selectedChapterId, setSelectedChapterId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accessRecords, setAccessRecords] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [invitingChapterId, setInvitingChapterId] = useState("");
  const [memberPanelMode, setMemberPanelMode] = useState("list");
  const [memberDetailMode, setMemberDetailMode] = useState("view");
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [memberDraft, setMemberDraft] = useState(defaultChapterMemberDraft);
  const [memberProfileDraft, setMemberProfileDraft] = useState(memberEditDraft);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const deferredSearch = useDeferredValue(filters.q);
  const activeRangeId = selectedRange?.rangeId || routeRangeId;
  const publicSlug = selectedRange?.slug || "";
  const canManageAccess = new Set(selectedRange?.access?.permissions || []).has("range:manage_access");
  const selectedChapter = useMemo(
    () => chapters.find((chapter) => chapter.chapterId === selectedChapterId) || chapters[0] || null,
    [chapters, selectedChapterId]
  );
  const selectedChapterLead = useMemo(() => chapterLead(selectedChapter || {}), [selectedChapter]);
  const selectedChapterLeaderAccess = useMemo(
    () => chapterLeaderAccess(selectedChapter, accessRecords),
    [accessRecords, selectedChapter]
  );
  const selectedChapterMembers = useMemo(() => (
    selectedChapter?.chapterId
      ? members
        .filter((member) => memberMatchesChapter(member, selectedChapter.chapterId))
        .sort((left, right) => String(left.fullName || left.email || "").localeCompare(String(right.fullName || right.email || "")))
      : []
  ), [members, selectedChapter?.chapterId]);
  const selectedMember = useMemo(() => (
    selectedChapterMembers.find((member) => member.memberId === selectedMemberId) || null
  ), [selectedChapterMembers, selectedMemberId]);
  const directoryChapters = useMemo(() => (
    chapters.filter((chapter) => chapterMatchesFilters(chapter, {
      q: deferredSearch,
      visibility: filters.visibility
    }))
  ), [chapters, deferredSearch, filters.visibility]);

  useEffect(() => {
    if (!activeRangeId) {
      setChapters([]);
      return;
    }

    let cancelled = false;
    async function loadChapters() {
      setLoading(true);
      setError("");
      try {
        const [ownerResult, publicResult] = await Promise.allSettled([
          listChapters(activeRangeId),
          publicSlug ? getPublicChapters(publicSlug) : Promise.resolve({ chapters: [] })
        ]);
        if (!cancelled) {
          const ownerChapters = ownerResult.status === "fulfilled" ? ownerResult.value?.chapters || [] : [];
          const publicChapters = publicResult.status === "fulfilled" ? publicResult.value?.chapters || [] : [];
          const mergedChapters = mergeChapterDirectories(
            [...eventChapterFallbacks(events), ...ownerChapters],
            publicChapters
          );
          setChapters(mergedChapters);
          setSelectedChapterId((current) => (
            current && mergedChapters.some((chapter) => chapter.chapterId === current)
              ? current
              : mergedChapters[0]?.chapterId || ""
          ));
          setError(ownerResult.status === "rejected" && publicResult.status === "rejected"
            ? ownerResult.reason?.message || "Chapters could not be loaded."
            : "");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadChapters();
    return () => {
      cancelled = true;
    };
  }, [activeRangeId, events, publicSlug]);

  useEffect(() => {
    if (!activeRangeId || !canManageAccess) {
      setAccessRecords([]);
      return;
    }

    let cancelled = false;
    async function loadChapterLeaderAccess() {
      setAccessLoading(true);
      try {
        const result = await listRangeAccess(activeRangeId, { role: "chapter_leader" });
        if (!cancelled) {
          setAccessRecords(result?.access || []);
        }
      } catch {
        if (!cancelled) {
          setAccessRecords([]);
        }
      } finally {
        if (!cancelled) {
          setAccessLoading(false);
        }
      }
    }

    loadChapterLeaderAccess();
    return () => {
      cancelled = true;
    };
  }, [activeRangeId, canManageAccess]);

  useEffect(() => {
    if (!selectedMember && memberPanelMode === "detail") {
      setMemberPanelMode("list");
      setSelectedMemberId("");
    }
  }, [memberPanelMode, selectedMember]);

  useEffect(() => {
    function syncChapterTabFromHash() {
      const nextTab = chapterTabFromHash(window.location.hash);
      setActiveChapterTab(nextTab);
      if (nextTab === "settings") {
        setChapterViewMode("list");
        setEditingChapterId("");
        setMemberPanelMode("list");
        setMemberDetailMode("view");
        setSelectedMemberId("");
        setDraft(defaultChapterDraft());
      }
    }

    syncChapterTabFromHash();
    window.addEventListener("hashchange", syncChapterTabFromHash);
    return () => window.removeEventListener("hashchange", syncChapterTabFromHash);
  }, []);

  useEffect(() => {
    setMemberDraft(defaultChapterMemberDraft(selectedChapter, membershipSummary));
  }, [membershipSummary, selectedChapter]);

  useEffect(() => {
    if (selectedMember) {
      setMemberProfileDraft(memberEditDraft(selectedMember));
    }
  }, [selectedMember]);

  function openCreate() {
    setActiveChapterTab("directory");
    setChapterViewMode("create");
    setEditingChapterId("");
    setDraft(defaultChapterDraft());
    setError("");
    setNotice("");
  }

  function openEdit(chapter) {
    setActiveChapterTab("directory");
    setChapterViewMode("edit");
    setEditingChapterId(chapter.chapterId);
    setDraft(chapterToDraft(chapter));
    setError("");
    setNotice("");
  }

  function openChapterDetail(chapter) {
    setSelectedChapterId(chapter.chapterId);
    setChapterViewMode("detail");
    setMemberPanelMode("list");
    setSelectedMemberId("");
    setError("");
    setNotice("");
  }

  function openAddMember() {
    setMemberDraft(defaultChapterMemberDraft(selectedChapter, membershipSummary));
    setMemberPanelMode("add");
    setMemberDetailMode("view");
    setSelectedMemberId("");
    setError("");
    setNotice("");
  }

  function openMemberDetail(member) {
    setSelectedMemberId(member.memberId);
    setMemberProfileDraft(memberEditDraft(member));
    setMemberPanelMode("detail");
    setMemberDetailMode("view");
    setError("");
    setNotice("");
  }

  function closeMemberPanel() {
    setMemberPanelMode("list");
    setMemberDetailMode("view");
    setSelectedMemberId("");
    setMemberProfileDraft(memberEditDraft());
    setMemberDraft(defaultChapterMemberDraft(selectedChapter, membershipSummary));
  }

  function activateChapterTab(tabId) {
    setActiveChapterTab(tabId);
    if (tabId === "settings") {
      setChapterViewMode("list");
      setEditingChapterId("");
      setMemberPanelMode("list");
      setMemberDetailMode("view");
      setSelectedMemberId("");
      setDraft(defaultChapterDraft());
    } else {
      setChapterViewMode("list");
    }
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#chapters-${tabId}`);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!activeRangeId) {
      setError("Open this chapter from a range before saving.");
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const payload = draftToPayload(draft);
      const saved = editingChapterId
        ? await updateChapter(activeRangeId, editingChapterId, payload)
        : await createChapter(activeRangeId, payload);
      setChapters((current) => {
        const exists = current.some((chapter) => chapter.chapterId === saved.chapterId);
        const next = exists
          ? current.map((chapter) => (chapter.chapterId === saved.chapterId ? saved : chapter))
          : [saved, ...current];
        return mergeChapterDirectories(next, []);
      });
      setSelectedChapterId(saved.chapterId);
      setActiveChapterTab("directory");
      setChapterViewMode("detail");
      setEditingChapterId("");
      setDraft(defaultChapterDraft());
      setNotice(`${saved.name} ${editingChapterId ? "updated" : "created"}.`);
    } catch (err) {
      setError(err.message || "Chapter could not be saved. Check the form and try again.");
    } finally {
      setSaving(false);
    }
  }

  async function handleInviteChapterLead(chapter) {
    if (!activeRangeId || !chapter?.chapterId) {
      return;
    }
    const lead = chapterLead(chapter);
    if (!availableLeadValue(lead.email)) {
      setError("Add the leader email before inviting them to the chapter workspace.");
      return;
    }

    setInvitingChapterId(chapter.chapterId);
    setError("");
    setNotice("");
    try {
      const existingLeadAccess = chapterLeaderAccessByEmail(lead.email, accessRecords);
      if (existingLeadAccess?.accessUserId) {
        const nextChapterIds = Array.from(new Set([...(existingLeadAccess.chapterIds || []), chapter.chapterId]));
        const nextApplicationScopes = Array.from(new Set([
          ...(existingLeadAccess.applicationScopes || []),
          ...CHAPTER_LEADER_APPLICATION_SCOPES
        ]));
        const updated = await updateRangeAccess(activeRangeId, existingLeadAccess.accessUserId, {
          role: existingLeadAccess.role || "chapter_leader",
          status: existingLeadAccess.status || "pending",
          scopeMode: "limited",
          chapterIds: nextChapterIds,
          applicationScopes: nextApplicationScopes
        });
        setAccessRecords((current) => [updated, ...current.filter((item) => item.accessUserId !== updated.accessUserId)]);
        setNotice(`${lead.name} now has chapter workspace access for ${chapter.name}.`);
        return;
      }
      const created = await grantRangeAccess(activeRangeId, {
        email: lead.email,
        role: "chapter_leader",
        scopeMode: "limited",
        chapterIds: [chapter.chapterId],
        applicationScopes: CHAPTER_LEADER_APPLICATION_SCOPES
      });
      setAccessRecords((current) => [created, ...current.filter((item) => item.accessUserId !== created.accessUserId)]);
      setNotice(`${lead.name} can sign in with ${lead.email} to open this chapter workspace.`);
    } catch (err) {
      const message = err.message || "Chapter leader access could not be created.";
      if (/already been granted/i.test(message)) {
        setNotice(`${lead.email} already has workspace access. Check Access if the scope needs to change.`);
      } else {
        setError(message);
      }
    } finally {
      setInvitingChapterId("");
    }
  }

  async function handleAddChapterMember(event) {
    event.preventDefault();
    if (!selectedChapter?.chapterId || !onCreateMember) {
      return;
    }
    setError("");
    setNotice("");
    try {
      const payload = {
        ...memberDraft,
        fullName: memberDraft.fullName.trim(),
        email: memberDraft.email.trim(),
        phone: memberDraft.phone.trim(),
        address: memberAddressPayload(memberDraft.address),
        homeChapterId: selectedChapter.chapterId,
        chapterIds: [selectedChapter.chapterId],
        chapterSnapshot: defaultChapterMemberDraft(selectedChapter, membershipSummary).chapterSnapshot
      };
      const created = await onCreateMember(payload);
      if (created?.memberId) {
        setSelectedMemberId(created.memberId);
        setMemberProfileDraft(memberEditDraft(created));
        setMemberPanelMode("detail");
        setMemberDetailMode("view");
      } else {
        setMemberPanelMode("list");
      }
      setMemberDraft(defaultChapterMemberDraft(selectedChapter, membershipSummary));
      setNotice(`${payload.fullName} was added to ${selectedChapter.name}.`);
    } catch (err) {
      setError(err.message || "Member could not be added.");
    }
  }

  async function handleSaveMemberProfile(event) {
    event.preventDefault();
    if (!selectedMember?.memberId || !onUpdateMemberProfile) {
      return;
    }
    setError("");
    setNotice("");
    try {
      await onUpdateMemberProfile(selectedMember.memberId, {
        fullName: memberProfileDraft.fullName.trim(),
        email: memberProfileDraft.email.trim(),
        phone: memberProfileDraft.phone.trim(),
        address: memberAddressPayload(memberProfileDraft.address),
        homeChapterId: selectedChapter?.chapterId,
        chapterIds: selectedChapter?.chapterId ? [selectedChapter.chapterId] : memberChapterIds(selectedMember),
        chapterSnapshot: selectedChapter?.chapterId
          ? defaultChapterMemberDraft(selectedChapter, membershipSummary).chapterSnapshot
          : selectedMember.chapterSnapshot
      });
      setMemberDetailMode("view");
      setNotice("Member profile updated.");
    } catch (err) {
      setError(err.message || "Member profile could not be updated.");
    }
  }

  async function handleToggleMemberStatus() {
    if (!selectedMember?.memberId || !onUpdateMemberStatus) {
      return;
    }
    const currentStatus = selectedMember.status || "active";
    const nextStatus = currentStatus === "active" ? "inactive" : "active";
    setError("");
    setNotice("");
    try {
      await onUpdateMemberStatus(selectedMember.memberId, {
        status: nextStatus,
        reason: nextStatus === "active"
          ? "Reactivated from chapter profile."
          : "Set inactive from chapter profile."
      });
      setNotice(`${selectedMember.fullName || selectedMember.email || "Member"} is now ${memberStatusLabel(nextStatus)}.`);
    } catch (err) {
      setError(err.message || "Member status could not be updated.");
    }
  }

  function renderMemberProfile() {
    if (!selectedMember) {
      return null;
    }
    const status = selectedMember.status || "active";
    const busyProfile = updatingMemberProfileId === selectedMember.memberId;
    const busyStatus = updatingMemberStatusId === selectedMember.memberId;
    const joinedLabel = formatMemberDate(memberJoinedAt(selectedMember));
    return (
      <section className="panel chapter-member-detail-panel" aria-label="Member profile">
        <div className="panel-header chapter-detail-header">
          <div>
            <span className="section-kicker">Member profile</span>
            <h2>{selectedMember.fullName || selectedMember.email || "Member"}</h2>
            <p>{selectedChapter?.name || selectedMember.chapterSnapshot?.name || "Chapter"} · Joined {joinedLabel}</p>
          </div>
          <div className="chapter-detail-actions">
            <button type="button" className="secondary-button" onClick={closeMemberPanel}>
              Back to chapter
            </button>
            {memberDetailMode === "edit" ? (
              <button
                type="button"
                className="secondary-button"
                onClick={() => {
                  setMemberProfileDraft(memberEditDraft(selectedMember));
                  setMemberDetailMode("view");
                }}
                disabled={busyProfile}
              >
                Cancel edit
              </button>
            ) : (
              <button type="button" className="secondary-button" onClick={() => setMemberDetailMode("edit")}>
                Edit member
              </button>
            )}
            <button
              type="button"
              className="secondary-button"
              onClick={handleToggleMemberStatus}
              disabled={busyStatus}
            >
              {busyStatus ? "Updating..." : status === "active" ? "Set inactive" : "Set active"}
            </button>
          </div>
        </div>
        <div className="chapter-member-profile-summary">
          <div>
            <span>Status</span>
            <strong>{memberStatusLabel(status)}</strong>
          </div>
          <div>
            <span>Chapter</span>
            <strong>{selectedChapter?.name || selectedMember.chapterSnapshot?.name || "Chapter"}</strong>
          </div>
          <div>
            <span>Joined</span>
            <strong>{joinedLabel}</strong>
          </div>
        </div>
        {memberDetailMode === "edit" ? (
          <form className="chapter-form chapter-member-profile-form" onSubmit={handleSaveMemberProfile}>
            <div className="chapter-form-grid">
              <label>
                <span>Full name</span>
                <input
                  value={memberProfileDraft.fullName}
                  onChange={(event) => setMemberProfileDraft((current) => ({ ...current, fullName: event.target.value }))}
                  required
                />
              </label>
              <label>
                <span>Email</span>
                <input
                  type="email"
                  value={memberProfileDraft.email}
                  onChange={(event) => setMemberProfileDraft((current) => ({ ...current, email: event.target.value }))}
                  placeholder="member@example.org"
                />
              </label>
              <label>
                <span>Phone</span>
                <input
                  type="tel"
                  value={memberProfileDraft.phone}
                  onChange={(event) => setMemberProfileDraft((current) => ({ ...current, phone: event.target.value }))}
                  placeholder="(555) 123-4567"
                />
              </label>
            </div>
            <div className="chapter-address-fields">
              <label className="full-span">
                <span>Street address</span>
                <input
                  value={memberProfileDraft.address.line1}
                  onChange={(event) => setMemberProfileDraft((current) => ({
                    ...current,
                    address: { ...current.address, line1: event.target.value }
                  }))}
                  placeholder="123 Main St"
                />
              </label>
              <label className="full-span">
                <span>Apartment, suite, or unit</span>
                <input
                  value={memberProfileDraft.address.line2}
                  onChange={(event) => setMemberProfileDraft((current) => ({
                    ...current,
                    address: { ...current.address, line2: event.target.value }
                  }))}
                  placeholder="Unit 4B"
                />
              </label>
              <label>
                <span>City</span>
                <input
                  value={memberProfileDraft.address.city}
                  onChange={(event) => setMemberProfileDraft((current) => ({
                    ...current,
                    address: { ...current.address, city: event.target.value }
                  }))}
                  placeholder="Seattle"
                />
              </label>
              <label>
                <span>State</span>
                <input
                  value={memberProfileDraft.address.state}
                  onChange={(event) => setMemberProfileDraft((current) => ({
                    ...current,
                    address: { ...current.address, state: event.target.value }
                  }))}
                  placeholder="WA"
                />
              </label>
              <label>
                <span>ZIP</span>
                <input
                  value={memberProfileDraft.address.postalCode}
                  onChange={(event) => setMemberProfileDraft((current) => ({
                    ...current,
                    address: { ...current.address, postalCode: event.target.value }
                  }))}
                  placeholder="98101"
                />
              </label>
              <label>
                <span>Country</span>
                <input
                  value={memberProfileDraft.address.country}
                  onChange={(event) => setMemberProfileDraft((current) => ({
                    ...current,
                    address: { ...current.address, country: event.target.value.toUpperCase().slice(0, 2) }
                  }))}
                  placeholder="US"
                />
              </label>
            </div>
            <div className="form-actions">
              <button type="submit" disabled={busyProfile || !memberProfileDraft.fullName.trim()}>
                {busyProfile ? "Saving..." : "Save member"}
              </button>
            </div>
          </form>
        ) : (
          <div className="chapter-member-profile-view">
            <div>
              <span>Full name</span>
              <strong>{selectedMember.fullName || "Unavailable"}</strong>
            </div>
            <div>
              <span>Email</span>
              <strong>{selectedMember.email || "Unavailable"}</strong>
            </div>
            <div>
              <span>Phone</span>
              <strong>{selectedMember.phone || "Unavailable"}</strong>
            </div>
            <div>
              <span>Address</span>
              <strong>{formatMemberAddress(selectedMember.address)}</strong>
            </div>
          </div>
        )}
      </section>
    );
  }

  function renderAddChapterMember() {
    const membershipTypes = membershipSummary?.membershipTypes || [];
    return (
      <form className="chapter-add-member-form" onSubmit={handleAddChapterMember}>
        <div className="chapter-form-grid">
          <label>
            <span>Full name</span>
            <input
              value={memberDraft.fullName}
              onChange={(event) => setMemberDraft((current) => ({ ...current, fullName: event.target.value }))}
              required
              placeholder="Jordan Lee"
            />
          </label>
          <label>
            <span>Email</span>
            <input
              type="email"
              value={memberDraft.email}
              onChange={(event) => setMemberDraft((current) => ({ ...current, email: event.target.value }))}
              placeholder="member@example.org"
            />
          </label>
          <label>
            <span>Phone</span>
            <input
              type="tel"
              value={memberDraft.phone}
              onChange={(event) => setMemberDraft((current) => ({ ...current, phone: event.target.value }))}
              placeholder="(555) 123-4567"
            />
          </label>
          <label>
            <span>Plan</span>
            <select
              value={memberDraft.membershipTypeId}
              onChange={(event) => setMemberDraft((current) => ({ ...current, membershipTypeId: event.target.value }))}
            >
              {membershipTypes.length === 0 ? (
                <option value="">Default plan</option>
              ) : membershipTypes.map((type) => (
                <option key={type.membershipTypeId} value={type.membershipTypeId}>
                  {type.name || type.membershipTypeId}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="chapter-address-fields">
          <label className="full-span">
            <span>Street address</span>
            <input
              value={memberDraft.address.line1}
              onChange={(event) => setMemberDraft((current) => ({
                ...current,
                address: { ...current.address, line1: event.target.value }
              }))}
              placeholder="123 Main St"
            />
          </label>
          <label className="full-span">
            <span>Apartment, suite, or unit</span>
            <input
              value={memberDraft.address.line2}
              onChange={(event) => setMemberDraft((current) => ({
                ...current,
                address: { ...current.address, line2: event.target.value }
              }))}
              placeholder="Unit 4B"
            />
          </label>
          <label>
            <span>City</span>
            <input
              value={memberDraft.address.city}
              onChange={(event) => setMemberDraft((current) => ({
                ...current,
                address: { ...current.address, city: event.target.value }
              }))}
              placeholder="Seattle"
            />
          </label>
          <label>
            <span>State</span>
            <input
              value={memberDraft.address.state}
              onChange={(event) => setMemberDraft((current) => ({
                ...current,
                address: { ...current.address, state: event.target.value }
              }))}
              placeholder="WA"
            />
          </label>
          <label>
            <span>ZIP</span>
            <input
              value={memberDraft.address.postalCode}
              onChange={(event) => setMemberDraft((current) => ({
                ...current,
                address: { ...current.address, postalCode: event.target.value }
              }))}
              placeholder="98101"
            />
          </label>
          <label>
            <span>Country</span>
            <input
              value={memberDraft.address.country}
              onChange={(event) => setMemberDraft((current) => ({
                ...current,
                address: { ...current.address, country: event.target.value.toUpperCase().slice(0, 2) }
              }))}
              placeholder="US"
            />
          </label>
        </div>
        <div className="form-actions">
          <button type="button" className="secondary-button" onClick={closeMemberPanel} disabled={savingMember}>
            Cancel
          </button>
          <button type="submit" disabled={savingMember || !memberDraft.fullName.trim()}>
            {savingMember ? "Adding..." : "Add member"}
          </button>
        </div>
      </form>
    );
  }

  function renderChapterForm() {
    return (
      <form className="chapter-form" onSubmit={handleSubmit}>
        <div className="chapter-form-grid">
          <label>
            <span>Chapter name</span>
            <input
              value={draft.name}
              onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
              required
              placeholder="Chicago Chapter"
            />
          </label>
          <label>
            <span>Public link</span>
            <input
              value={draft.slug}
              onChange={(event) => setDraft((current) => ({ ...current, slug: event.target.value }))}
              placeholder="Auto-filled from chapter name"
            />
          </label>
          <label>
            <span>City</span>
            <input
              value={draft.city}
              onChange={(event) => setDraft((current) => ({ ...current, city: event.target.value }))}
              placeholder="Chicago"
            />
          </label>
          <label>
            <span>State or region</span>
            <input
              value={draft.region}
              onChange={(event) => setDraft((current) => ({ ...current, region: event.target.value }))}
              placeholder="IL"
            />
          </label>
          <label>
            <span>Leader first name</span>
            <input
              value={draft.leaderFirstName}
              onChange={(event) => setDraft((current) => ({ ...current, leaderFirstName: event.target.value }))}
              placeholder="Alex"
            />
          </label>
          <label>
            <span>Leader last name</span>
            <input
              value={draft.leaderLastName}
              onChange={(event) => setDraft((current) => ({ ...current, leaderLastName: event.target.value }))}
              placeholder="Morgan"
            />
          </label>
          <label>
            <span>Leader email</span>
            <input
              type="email"
              value={draft.leaderEmailsText}
              onChange={(event) => setDraft((current) => ({ ...current, leaderEmailsText: event.target.value }))}
              placeholder="leader@example.org"
            />
          </label>
          <label>
            <span>Leader phone</span>
            <input
              type="tel"
              value={draft.leaderPhone}
              onChange={(event) => setDraft((current) => ({ ...current, leaderPhone: event.target.value }))}
              placeholder="(555) 123-4567"
            />
          </label>
          <label>
            <span>Contact email</span>
            <input
              type="email"
              value={draft.contactEmail}
              onChange={(event) => setDraft((current) => ({ ...current, contactEmail: event.target.value }))}
              placeholder="chapter@example.org"
            />
          </label>
          <label>
            <span>Contact phone</span>
            <input
              type="tel"
              value={draft.contactPhone}
              onChange={(event) => setDraft((current) => ({ ...current, contactPhone: event.target.value }))}
              placeholder="(555) 123-4567"
            />
          </label>
          <label>
            <span>Venue name</span>
            <input
              value={draft.venueName}
              onChange={(event) => setDraft((current) => ({ ...current, venueName: event.target.value }))}
              placeholder="Stock and Barrel Gun Club Eagan"
            />
          </label>
          <label>
            <span>Status</span>
            <select
              value={draft.status}
              onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value }))}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </label>
          <label>
            <span>Visibility</span>
            <select
              value={draft.publicVisible ? "public" : "private"}
              onChange={(event) => setDraft((current) => ({
                ...current,
                publicVisible: event.target.value === "public"
              }))}
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>
          </label>
        </div>
        <div className="chapter-address-fields">
          <label className="full-span">
            <span>Street address</span>
            <input
              value={draft.address.line1}
              onChange={(event) => setDraft((current) => ({
                ...current,
                address: { ...current.address, line1: event.target.value }
              }))}
              placeholder="2980 Commers Drive #800"
            />
          </label>
          <label className="full-span">
            <span>Address line 2</span>
            <input
              value={draft.address.line2}
              onChange={(event) => setDraft((current) => ({
                ...current,
                address: { ...current.address, line2: event.target.value }
              }))}
              placeholder="Suite, classroom, or unit"
            />
          </label>
          <label>
            <span>Address city</span>
            <input
              value={draft.address.city}
              onChange={(event) => setDraft((current) => ({
                ...current,
                address: { ...current.address, city: event.target.value }
              }))}
              placeholder="Eagan"
            />
          </label>
          <label>
            <span>Address state</span>
            <input
              value={draft.address.region}
              onChange={(event) => setDraft((current) => ({
                ...current,
                address: { ...current.address, region: event.target.value }
              }))}
              placeholder="MN"
            />
          </label>
          <label>
            <span>Postal code</span>
            <input
              value={draft.address.postalCode}
              onChange={(event) => setDraft((current) => ({
                ...current,
                address: { ...current.address, postalCode: event.target.value }
              }))}
              placeholder="55121"
            />
          </label>
          <label>
            <span>Country</span>
            <input
              value={draft.address.country}
              onChange={(event) => setDraft((current) => ({
                ...current,
                address: { ...current.address, country: event.target.value.toUpperCase().slice(0, 2) }
              }))}
              placeholder="US"
            />
          </label>
        </div>
        <label>
          <span>Location note</span>
          <input
            value={draft.meetingArea}
            onChange={(event) => setDraft((current) => ({ ...current, meetingArea: event.target.value }))}
            placeholder="Primary range, classroom, or city area"
          />
        </label>
        <label>
          <span>Short public summary</span>
          <input
            value={draft.summary}
            onChange={(event) => setDraft((current) => ({ ...current, summary: event.target.value }))}
            placeholder="Monthly training and community chapter in the Twin Cities."
          />
        </label>
        <label>
          <span>Description</span>
          <textarea
            value={draft.description}
            onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
            rows={4}
            placeholder="What members should know about this chapter."
          />
        </label>
        <div className="chapter-form-grid">
          <label>
            <span>Public URL</span>
            <input
              type="url"
              value={draft.publicUrl}
              onChange={(event) => setDraft((current) => ({ ...current, publicUrl: event.target.value }))}
              placeholder="https://loxandloaded.org"
            />
          </label>
          <label>
            <span>Source URL</span>
            <input
              type="url"
              value={draft.sourceUrl}
              onChange={(event) => setDraft((current) => ({ ...current, sourceUrl: event.target.value }))}
              placeholder="https://loxandloaded.org/product/..."
            />
          </label>
        </div>
        <div className="form-actions">
          <button
            type="button"
            className="secondary-button"
            onClick={() => {
              const returnToDetail = Boolean(editingChapterId && selectedChapterId);
              setEditingChapterId("");
              setDraft(defaultChapterDraft());
              setActiveChapterTab("directory");
              setChapterViewMode(returnToDetail ? "detail" : "list");
            }}
            disabled={saving}
          >
            Cancel
          </button>
          <button type="submit" disabled={saving}>
            {saving ? "Saving..." : editingChapterId ? "Save chapter" : "Create chapter"}
          </button>
        </div>
      </form>
    );
  }

  function updateChapterMemberVisibility(key, value) {
    if (!setMembershipConfigForm) {
      return;
    }
    setMembershipConfigForm((current) => ({
      ...current,
      [key]: value
    }));
  }

  function renderChapterMemberAccessSettings() {
    const lockedFields = [
      { label: "Name", detail: "Always visible" },
      { label: "Email", detail: "Always visible" }
    ];
    const visibleOptionalCount = CHAPTER_MEMBER_VISIBILITY_FIELDS
      .filter((field) => Boolean(membershipConfigForm[field.key]))
      .length;
    return (
      <form className="chapter-member-access-panel" onSubmit={onSaveMembershipConfig}>
        <div className="panel-header chapter-member-access-header">
          <div>
            <span className="section-kicker">Owner setting</span>
            <h2>Chapter lead view</h2>
            <p>Choose what every chapter lead can see in member profiles.</p>
          </div>
          <div className="chapter-member-access-save">
            <span>{visibleOptionalCount} optional {visibleOptionalCount === 1 ? "field" : "fields"} visible</span>
            <button type="submit" disabled={savingMembershipConfig || !onSaveMembershipConfig}>
              {savingMembershipConfig ? "Saving..." : "Save access"}
            </button>
          </div>
        </div>
        <div className="chapter-member-access-body">
          <section className="chapter-member-access-group">
            <div className="chapter-member-access-group-title">
              <span>Always visible</span>
              <strong>Basic identity</strong>
            </div>
            <div className="chapter-member-access-grid is-locked-group">
              {lockedFields.map((field) => (
                <div className="chapter-member-access-row is-locked" key={field.label}>
                  <span>
                    <strong>{field.label}</strong>
                    <small>{field.detail}</small>
                  </span>
                  <em>Required</em>
                </div>
              ))}
            </div>
          </section>
          <section className="chapter-member-access-group">
            <div className="chapter-member-access-group-title">
              <span>Optional</span>
              <strong>Member details</strong>
            </div>
            <div className="chapter-member-access-grid">
              {CHAPTER_MEMBER_VISIBILITY_FIELDS.map((field) => (
                <label className="chapter-member-access-row" key={field.key}>
                  <span>
                    <strong>{field.label}</strong>
                    <small>{field.detail}</small>
                  </span>
                  <span className="chapter-member-access-switch">
                    <input
                      type="checkbox"
                      checked={Boolean(membershipConfigForm[field.key])}
                      onChange={(event) => updateChapterMemberVisibility(field.key, event.target.checked)}
                    />
                    <i />
                  </span>
                </label>
              ))}
            </div>
          </section>
        </div>
      </form>
    );
  }

  function renderChapterDetail() {
    return (
      <section className="panel chapter-detail-panel is-focused" aria-label="Chapter detail">
        {selectedChapter ? (
          <>
            <div className="panel-header chapter-detail-header">
              <div>
                <span className="section-kicker">Chapter profile</span>
                <h2>{selectedChapter.name}</h2>
              </div>
              <div className="chapter-detail-actions">
                <button type="button" className="secondary-button" onClick={() => setChapterViewMode("list")}>
                  Back to directory
                </button>
                <button type="button" className="secondary-button" onClick={() => openEdit(selectedChapter)}>
                  Edit chapter
                </button>
              </div>
            </div>
            <div className="chapter-detail-grid">
              <div>
                <span>Location</span>
                <strong>{chapterLocation(selectedChapter)}</strong>
              </div>
              <div>
                <span>Venue</span>
                <strong>{selectedChapter.venueName || selectedChapter.meetingArea || "Unavailable"}</strong>
              </div>
              <div>
                <span>Address</span>
                <strong>{formatChapterAddress(selectedChapter.address)}</strong>
              </div>
              <div>
                <span>Visibility</span>
                <strong>{chapterVisibility(selectedChapter)}</strong>
              </div>
              <div className="chapter-lead-detail">
                <span>Lead</span>
                <strong>{selectedChapterLead.name}</strong>
                {selectedChapterLead.available && availableLeadValue(selectedChapterLead.email) && (
                  <small>{selectedChapterLead.email}</small>
                )}
                {selectedChapterLead.available && availableLeadValue(selectedChapterLead.phone) && (
                  <small>{selectedChapterLead.phone}</small>
                )}
                {selectedChapterLeaderAccess ? (
                  <small>Workspace access: {accessStatusLabel(selectedChapterLeaderAccess.status)}</small>
                ) : selectedChapterLead.available && availableLeadValue(selectedChapterLead.email) ? (
                  <button
                    type="button"
                    className="secondary-button compact-button"
                    disabled={!canManageAccess || accessLoading || invitingChapterId === selectedChapter.chapterId}
                    onClick={() => handleInviteChapterLead(selectedChapter)}
                  >
                    {invitingChapterId === selectedChapter.chapterId ? "Inviting..." : "Invite leader"}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="secondary-button compact-button"
                    onClick={() => openEdit(selectedChapter)}
                  >
                    Add leader email
                  </button>
                )}
              </div>
              <div>
                <span>Contact</span>
                <strong>{selectedChapter.contactEmail || "Unavailable"}</strong>
                {selectedChapter.contactPhone && <small>{selectedChapter.contactPhone}</small>}
              </div>
              <div>
                <span>Public link</span>
                <strong>{publicPath(selectedChapter)}</strong>
              </div>
              <div>
                <span>Public URL</span>
                <strong>{selectedChapter.publicUrl || "Unavailable"}</strong>
              </div>
              <div>
                <span>Source</span>
                <strong>{selectedChapter.sourceUrl || "Unavailable"}</strong>
              </div>
              <div>
                <span>Summary</span>
                <strong>{selectedChapter.summary || selectedChapter.description || "Unavailable"}</strong>
              </div>
              <div>
                <span>Members</span>
                <strong>{selectedChapterMembers.length}</strong>
              </div>
            </div>

            <div className="chapter-members-panel">
              <div className="chapter-members-header">
                <div>
                  <strong>Members in this chapter</strong>
                  <span>{membersLoading ? "Loading" : `${selectedChapterMembers.length} members`}</span>
                </div>
                {memberPanelMode === "list" ? (
                  <button type="button" className="secondary-button compact-button" onClick={openAddMember}>
                    Add member
                  </button>
                ) : null}
              </div>
              {memberPanelMode === "add" ? (
                renderAddChapterMember()
              ) : membersLoading ? (
                <div className="empty-state">Loading members...</div>
              ) : selectedChapterMembers.length === 0 ? (
                <div className="empty-state">
                  <strong>No members assigned.</strong>
                  <span>Members appear here after their home chapter is set.</span>
                </div>
              ) : (
                <div className="chapter-member-list">
                  {selectedChapterMembers.map((member) => (
                    <button
                      type="button"
                      className="chapter-member-row chapter-member-row-button"
                      key={member.memberId || member.email}
                      onClick={() => openMemberDetail(member)}
                    >
                      <span>
                        <strong>{member.fullName || member.email || "Member"}</strong>
                        <small>{member.email || "No email"}</small>
                        <small>Joined {formatMemberDate(memberJoinedAt(member))}</small>
                      </span>
                      <span className={`member-status status-${memberStatus(member)}`}>
                        {memberStatusLabel(memberStatus(member))}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="empty-state">
            <strong>Select a chapter.</strong>
            <span>Chapter details and members will appear here.</span>
          </div>
        )}
      </section>
    );
  }

  return (
    <div className="chapters-page">
      {loading && <div className="status-strip">Refreshing chapters...</div>}
      {notice && <div className="status-strip" role="status">{notice}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {memberPanelMode === "detail" && selectedMember ? renderMemberProfile() : (
        <>

      <section className="panel chapters-command-panel">
        <div className="panel-header">
          <div>
            <h2>Chapters</h2>
          </div>
          <button type="button" onClick={openCreate}>
            New chapter
          </button>
        </div>
        <div className="chapters-tabbar is-simple" role="tablist" aria-label="Chapter workspace sections">
          {[
            { id: "directory", label: "Directory", detail: `${chapters.length} chapters` },
            { id: "settings", label: "Settings", detail: "Chapter lead view" }
          ].map((tab) => (
            <button
              type="button"
              key={tab.id}
              className={activeChapterTab === tab.id ? "is-active" : ""}
              onClick={() => activateChapterTab(tab.id)}
            >
              <span>{tab.label}</span>
              <small>{tab.detail}</small>
            </button>
          ))}
        </div>
      </section>

      {activeChapterTab === "directory" && (
        <>
          {chapterViewMode === "create" || chapterViewMode === "edit" ? (
            <div className="chapter-setup-stack">
              <section className="panel chapter-editor-panel">
                <div className="panel-header">
                  <div>
                    <span className="section-kicker">{chapterViewMode === "edit" ? "Edit chapter" : "New chapter"}</span>
                    <h2>{editingChapterId ? "Edit chapter" : "Create chapter"}</h2>
                  </div>
                </div>
                {renderChapterForm()}
              </section>
            </div>
          ) : chapterViewMode === "list" ? (
            <>
              <section className="panel chapters-directory-panel">
                <div className="leads-toolbar chapters-toolbar">
                  <label>
                    <span>Search</span>
                    <input
                      type="search"
                      value={filters.q}
                      onChange={(event) => setFilters((current) => ({ ...current, q: event.target.value }))}
                      placeholder="Name, location, or lead"
                    />
                  </label>
                  <label>
                    <span>Visibility</span>
                    <select
                      value={filters.visibility}
                      onChange={(event) => setFilters((current) => ({ ...current, visibility: event.target.value }))}
                    >
                      <option value="">All chapters</option>
                      <option value="public">Public</option>
                      <option value="private">Private</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </label>
                  {(filters.q || filters.visibility) && (
                    <button type="button" className="secondary-button" onClick={() => setFilters({ q: "", visibility: "" })}>
                      Clear
                    </button>
                  )}
                </div>
              </section>

              <section className="chapters-list is-directory" aria-label="Chapters">
              {directoryChapters.length === 0 && !loading ? (
                <div className="empty-state">
                  <strong>No chapters match this view.</strong>
                  <span>Clear the filters or search a different location.</span>
                </div>
              ) : directoryChapters.map((chapter) => {
                const lead = chapterLead(chapter);
                const selected = selectedChapter?.chapterId === chapter.chapterId;
                return (
                  <button
                    type="button"
                    className={`chapter-row chapter-row-button chapter-row-${chapter.status} ${selected ? "is-selected" : ""}`}
                    key={chapter.chapterId || chapter.slug || chapter.name}
                    onClick={() => openChapterDetail(chapter)}
                  >
                    <span className="chapter-row-main">
                      <span>
                        <span className="chapter-location">{chapterLocation(chapter)}</span>
                        <strong>{chapter.name}</strong>
                      </span>
                      <span className="chapter-status-stack">
                        <span className="chapter-public-state">{chapterVisibility(chapter)}</span>
                      </span>
                    </span>
                    <span className="chapter-row-meta">
                      <span>
                        <strong>{lead.name}</strong>
                        {lead.available && availableLeadValue(lead.email) && <small>{lead.email}</small>}
                        {lead.available && availableLeadValue(lead.phone) && <small>{lead.phone}</small>}
                      </span>
                    </span>
                  </button>
                );
              })}
              </section>
            </>
          ) : renderChapterDetail()}
        </>
      )}

      {activeChapterTab === "settings" && (
        <div className="chapter-settings-stack">
          {renderChapterMemberAccessSettings()}
        </div>
      )}
        </>
      )}
    </div>
  );
}
